/**
 * Öğretmen / rehber öğretmen portalı.
 *
 * Tasarım kararı: burası klinik bir ekran değil ve okul tarafına
 * açılan tek pencere. Öğretmen yalnızca ATANDIĞI öğrencileri, kendisine
 * yazılmış notu ve doldurması istenen formları görüyor. Tanı, ölçüm
 * puanı, rapor, seans notu, randevu, veli bilgisi ve kendi doldurduğu
 * formun puanı yok — hiçbiri sunucudan dönmüyor bile.
 *
 * Girişi psikolog veriyor: atamayı o yapıyor, veli onamını o alıyor.
 * Atama kapandığı ya da onam geri çekildiği an bu ekran boşalıyor.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  KAPI_ADI,
  api,
  kapiyiBul,
  tarih,
  type FormIstegi,
  type FormTanimi,
  type Gecmis,
  type DavranisHedefi,
  type Ogrenci,
  type Ogretmen,
} from './api'
import { Giris } from './pages/Giris'
import { GozlemFormu } from './pages/Form'
import { Davranislar } from './pages/Davranis'

const kapi = kapiyiBul()

export function App() {
  const [ogretmen, setOgretmen] = useState<Ogretmen | null>(null)
  const [yukleniyor, setYukleniyor] = useState(true)
  const [ogrenciler, setOgrenciler] = useState<Ogrenci[] | null>(null)
  const [formlar, setFormlar] = useState<FormIstegi[]>([])
  const [tanimlar, setTanimlar] = useState<Record<string, FormTanimi>>({})
  const [gecmis, setGecmis] = useState<Gecmis[]>([])
  const [davranislar, setDavranislar] = useState<DavranisHedefi[]>([])
  const [buHafta, setBuHafta] = useState('')
  const [dolduruluyor, setDolduruluyor] = useState<FormIstegi | null>(null)
  const [bildirim, setBildirim] = useState<string | null>(null)
  /**
   * Veri yüklenemedi.
   *
   * Eskiden iki ayrı biçimde kayboluyordu: `ben()` zincirinin
   * `catch`i `setOgretmen(null)` yapıyordu (yani dört uçtan biri
   * düşünce geçerli oturumu olan öğretmen giriş ekranına atılıyordu),
   * ve girişten sonraki yükleme düşerse ekran sonsuza kadar
   * "Yükleniyor…" yazıyordu. İkisinde de ne hata ne tekrar deneme
   * vardı.
   */
  const [yuklemeHatasi, setYuklemeHatasi] = useState(false)

  const yukle = useCallback(async () => {
    const [o, f, g, dv] = await Promise.all([
      api.ogrenciler(),
      api.formlar(),
      api.gecmis(),
      api.davranislar(),
    ])
    setOgrenciler(o.ogrenciler)
    setFormlar(f.formlar)
    setTanimlar(f.tanimlar)
    setGecmis(g.gecmis)
    setDavranislar(dv.hedefler)
    setBuHafta(dv.buHafta)
  }, [])

  useEffect(() => {
    api
      .ben()
      .then(async (r) => {
        setOgretmen(r.ogretmen)
        /*
         * Veri yüklemesinin hatası OTURUMU DÜŞÜRMÜYOR: oturum
         * geçerli, yalnızca bir uç cevap vermedi.
         */
        try {
          await yukle()
        } catch {
          setYuklemeHatasi(true)
        }
      })
      .catch(() => setOgretmen(null))
      .finally(() => setYukleniyor(false))
  }, [yukle])


  /**
   * Sekme öne gelince tazele.
   *
   * Yükleyici yalnızca değişmeyen bir kimliğe bağlı; ilk açılışta bir
   * kez koşuyor ve ekran açık kaldığı sürece bir daha hiç veri
   * çekmiyordu. Bu ekranlar tam da açık bırakılan ekranlar: veli
   * telefonunda sekmeyi açık bırakıyor, uygulayıcı o sırada ödev
   * tanımlıyor ya da rapor gönderiyor, veli hâlâ "henüz bir şey yok"
   * görüyor.
   *
   * Aralıklı yoklama YAPILMIYOR: sunucuda hız sınırı var ve kimsenin
   * bakmadığı bir sekme için saatlerce istek atmak doğru değil.
   * Tazeleme, kullanıcı ekrana GERİ DÖNDÜĞÜNDE — yani bayatlığın
   * önem kazandığı anda — bir kez oluyor.
   */
  useEffect(() => {
    const geriDon = () => {
      if (document.visibilityState === 'visible') void yukle().catch(() => setYuklemeHatasi(true))
    }
    document.addEventListener('visibilitychange', geriDon)
    window.addEventListener('focus', geriDon)
    return () => {
      document.removeEventListener('visibilitychange', geriDon)
      window.removeEventListener('focus', geriDon)
    }
  }, [yukle])

  const cikis = useCallback(async () => {
    await api.cikis().catch(() => undefined)
    setOgretmen(null)
    setOgrenciler(null)
    setFormlar([])
    setGecmis([])
    setDavranislar([])
    setDolduruluyor(null)
  }, [])

  if (yukleniyor) {
    return (
      <div className="icerik">
        <p className="alt">Yükleniyor…</p>
      </div>
    )
  }

  if (!ogretmen) {
    return (
      <Giris
        kapi={kapi}
        onGiris={async (o) => {
          setOgretmen(o)
          setYukleniyor(true)
          setYuklemeHatasi(false)
          try {
            await yukle()
          } catch {
            setYuklemeHatasi(true)
          } finally {
            setYukleniyor(false)
          }
        }}
      />
    )
  }

  if (dolduruluyor) {
    const tanim = tanimlar[dolduruluyor.kod]
    return (
      <div className="kabuk">
        <Ust ogretmen={ogretmen} onCikis={cikis} />
        <main className="icerik">
          {!tanim ? (
            <div className="uyari">Bu form tanımı yüklenemedi. Sayfayı yenileyin.</div>
          ) : (
            <GozlemFormu
              tanim={tanim}
              ustBilgi={
                <div className="kart">
                  <div>
                    <strong>
                      {dolduruluyor.ad} {dolduruluyor.soyad}
                    </strong>{' '}
                    için dolduruyorsunuz.
                  </div>
                  {dolduruluyor.not && (
                    <p className="alt" style={{ marginBottom: 0 }}>
                      Merkezin notu: {dolduruluyor.not}
                    </p>
                  )}
                </div>
              }
              onGonder={async (cevaplar) => {
                await api.gonder(dolduruluyor.id, cevaplar)
                setDolduruluyor(null)
                setBildirim(
                  `${dolduruluyor.ad} ${dolduruluyor.soyad} için form gönderildi. Teşekkürler.`,
                )
                /*
                 * Form GİTTİ; tazeleme düşerse bu, gönderimi geçersiz
                 * kılmıyor. Korumasız bırakılırsa reddedilen söz
                 * yukarı kaçıyor ve öğretmen "gönderildi" yazısını
                 * görürken arkada bir hata duruyordu.
                 */
                await yukle().catch(() => setYuklemeHatasi(true))
              }}
              onIptal={() => setDolduruluyor(null)}
            />
          )}
        </main>
      </div>
    )
  }

  return (
    <div className="kabuk">
      <Ust ogretmen={ogretmen} onCikis={cikis} />
      <main className="icerik">
        {bildirim && <div className="bilgi">{bildirim}</div>}

        {yuklemeHatasi ? (
          <div className="uyari">
            Bilgiler alınamadı. Bağlantınızı kontrol edip tekrar deneyin.
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => {
                  setYuklemeHatasi(false)
                  void yukle().catch(() => setYuklemeHatasi(true))
                }}
              >
                Tekrar dene
              </button>
            </div>
          </div>
        ) : !ogrenciler ? (
          <p className="alt">Yükleniyor…</p>
        ) : ogrenciler.length === 0 ? (
          <div className="bilgi">
            Size atanmış öğrenci yok. Bu ekranı merkez açıyor: bir öğrenci için görüşünüz
            isteniyorsa merkez sizi o öğrenciye atadığında burada görünür.
          </div>
        ) : (
          <>
            <h1>Öğrencilerim</h1>
            <p className="alt">
              Yalnızca merkezin sizi atadığı öğrenciler görünüyor. Dosya, rapor ve ölçüm
              sonuçları bu ekranda yer almıyor. Girdiğiniz sayıları yalnızca siz
              görüyorsunuz; velinin ya da merkezin aynı hafta için yazdığı sayı burada
              görünmüyor.
            </p>

            {ogrenciler.map((o) => {
              const bekleyen = formlar.filter((f) => f.danisanId === o.id)
              const dolu = gecmis.filter((g) => g.danisanId === o.id)
              return (
                <div className="kart" key={o.id}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 12,
                    }}
                  >
                    <h2 style={{ margin: 0 }}>
                      {o.ad} {o.soyad}
                    </h2>
                    {bekleyen.length > 0 && (
                      <span className="etiket uyar">{bekleyen.length} form bekliyor</span>
                    )}
                  </div>

                  {o.not && (
                    <div className="bilgi" style={{ marginTop: 10 }}>
                      <strong>Merkezin size notu</strong>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{o.not}</div>
                      <p className="alt" style={{ margin: '6px 0 0' }}>
                        {tarih(o.notTarih)}
                      </p>
                    </div>
                  )}

                  {bekleyen.map((f) => (
                    <div
                      key={f.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        marginTop: 10,
                      }}
                    >
                      <div>
                        <div>{tanimlar[f.kod]?.ad ?? f.kod}</div>
                        <p className="alt" style={{ margin: 0 }}>
                          İstendi: {tarih(f.istendi)}
                          {f.bitis ? ` · Son gün: ${tarih(f.bitis)}` : ''}
                        </p>
                      </div>
                      <button className="birincil" onClick={() => setDolduruluyor(f)}>
                        Doldur
                      </button>
                    </div>
                  ))}

                  {bekleyen.length === 0 && (
                    <p className="alt" style={{ marginBottom: 0 }}>
                      Şu an doldurmanız istenen bir form yok.
                    </p>
                  )}

                  {dolu.length > 0 && (
                    <p className="alt" style={{ marginBottom: 0 }}>
                      Daha önce gönderdikleriniz: {dolu.map((d) => tarih(d.tarih)).join(', ')}
                    </p>
                  )}

                  {buHafta && (
                    <Davranislar
                      hedefler={davranislar.filter((d) => d.danisanId === o.id)}
                      buHafta={buHafta}
                      onKaydedildi={() => void yukle()}
                    />
                  )}
                </div>
              )
            })}
          </>
        )}
      </main>
    </div>
  )
}

function Ust({ ogretmen, onCikis }: { ogretmen: Ogretmen; onCikis: () => void }) {
  return (
    <header className="ust">
      <span className="marka">Nirengi</span>
      <span className="alt" style={{ margin: 0 }}>
        {KAPI_ADI[ogretmen.tur] ?? KAPI_ADI[kapi]}
      </span>
      <div className="sag">
        <span>{ogretmen.adSoyad}</span>
        <button onClick={onCikis}>Çıkış</button>
      </div>
    </header>
  )
}
